import { Suspense, lazy, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import useImage from '../../hooks/use-image';
import { useGetMy, useUpdateMy, useWithdraw } from '../../query/auth';
import { useGetDepartments } from '../../query/department';
import { useGetSchools } from '../../query/school';
import InputField from '../fields/input-field';
import SelectField from '../fields/select-field';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Form } from '../ui/form';

const Camera = lazy(() => import('lucide-react').then(mod => ({ default: mod.Camera })));
const CircleUserRound = lazy(() =>
  import('lucide-react').then(mod => ({ default: mod.CircleUserRound })),
);

const formSchema = z.object({
  profileUrl: z.string().optional(),
  school: z.string().optional(),
  department: z.string().optional(),
  departmentName: z.string().optional(),
  studentNumber: z.string().optional(),
  name: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const MyForm = () => {
  const navigate = useNavigate();
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      profileUrl: '',
      school: '',
      department: '',
      departmentName: '',
      studentNumber: '',
      name: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { resizeAndUpload, loading } = useImage();

  const { mutate: updateMy, isPending: isUpdatingMy } = useUpdateMy();
  const { mutate: withdraw, isPending: isWithdrawing } = useWithdraw({
    onSuccess: () => {
      toast.success('회원 탈퇴가 완료되었습니다.');
      navigate('/sign-in');
    },
    onError: () => {
      toast.error('회원 탈퇴에 실패했습니다.');
    },
  });
  const { data: myInfo } = useGetMy();
  const { data: schools } = useGetSchools();
  const { data: departments } = useGetDepartments();
  const { profileUrl } = form.watch();

  useEffect(() => {
    if (!myInfo) return;
    const departmentExists = departments?.data?.some(
      dept => dept.name === myInfo?.data?.department,
    );
    form.reset({
      profileUrl: myInfo?.data?.profileUrl,
      school: myInfo?.data?.school,
      department: departmentExists ? myInfo?.data?.department : '직접입력',
      departmentName: departmentExists ? '' : myInfo?.data?.department,
      studentNumber: myInfo?.data?.studentNumber,
      name: myInfo?.data?.name,
    });
  }, [myInfo, departments]);

  const onSubmit = (data: FormData) => {
    updateMy(
      {
        profileUrl: data.profileUrl,
        school: data.school,
        department: data.department === '직접입력' ? (data.departmentName ?? '') : data.department,
        studentNumber: data.studentNumber,
        name: data.name,
      },
      {
        onSuccess: () => {
          toast.success('정보가 수정되었습니다.');
        },
        onError: () => {
          toast.error('정보 수정에 실패했습니다.');
        },
      },
    );
  };

  return (
    <Card className="w-[500px] p-4 mx-auto">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4 w-full">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="w-[100px] h-[100px] relative">
              {profileUrl ? (
                <img
                  src={profileUrl}
                  alt={'프로필 이미지'}
                  width={100}
                  height={100}
                  className="rounded-full aspect-square"
                />
              ) : (
                <Suspense fallback={<div className="w-[100px] h-[100px]" />}>
                  <CircleUserRound width={100} height={100} strokeWidth={1} />
                </Suspense>
              )}
              <div className="absolute bottom-[4px] right-[4px] rounded-full border bg-white p-1">
                <label htmlFor="profile_image" className="cursor-pointer">
                  <Suspense fallback={<div className="w-[18px] h-[18px]" />}>
                    <Camera width={18} height={18} />
                  </Suspense>
                </label>
              </div>
            </div>
            <input
              type="file"
              className="text-sm hidden"
              id="profile_image"
              multiple={false}
              accept="image/*"
              onChange={async e => {
                const file = e.target.files?.[0];
                if (!file) return;

                const url = await resizeAndUpload(file, 'profile');
                form.setValue('profileUrl', url ?? '');
              }}
            />
            {loading && <div>업로드 중...</div>}
          </div>
          <SelectField
            control={form.control}
            name="school"
            placeholder="학교를 선택해주세요."
            label="학교"
            options={
              schools?.data?.map(school => ({
                label: school.name,
                value: school.name,
              })) ?? []
            }
          />
          <SelectField
            control={form.control}
            name="department"
            placeholder="학과를 선택해주세요."
            label="학과"
            options={[
              ...(departments?.data?.map(department => ({
                label: department.name,
                value: department.name,
              })) ?? []),
              { label: '직접입력', value: '직접입력' },
            ]}
          />

          {form.watch('department') === '직접입력' && (
            <InputField
              control={form.control}
              name="departmentName"
              placeholder="학과를 입력해주세요."
              label="학과 직접입력"
              type="text"
            />
          )}
          <InputField
            control={form.control}
            name="studentNumber"
            placeholder="학번을 입력해주세요."
            label="학번"
            type="text"
          />
          <InputField
            control={form.control}
            name="name"
            placeholder="이름을 입력해주세요."
            label="이름"
            type="text"
          />

          <div className="flex justify-center items-center">
            <Button type="submit" disabled={isUpdatingMy}>수정하기</Button>
          </div>
        </form>
      </Form>

      <div className="mt-8 pt-4 border-t">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              회원 탈퇴
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>회원 탈퇴</AlertDialogTitle>
              <AlertDialogDescription>
                정말 탈퇴하시겠습니까? 탈퇴 후에는 계정을 복구할 수 없습니다.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>취소</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => withdraw()}
                disabled={isWithdrawing}
                className="bg-red-500 hover:bg-red-600"
              >
                {isWithdrawing ? '처리중...' : '탈퇴하기'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Card>
  );
};

export default MyForm;
